import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { clientService } from '../services/api';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    status: 'active',
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const data = await clientService.getAll();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const created = await clientService.create(newClient);
      setClients([created, ...clients]);
      setShowAddModal(false);
      setNewClient({ name: '', company: '', email: '', phone: '', status: 'active' });
    } catch (error) {
      console.error('Error creating client:', error);
      alert('Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      inactive: 'badge-gray',
      prospect: 'badge-info',
    };
    return badges[status] || 'badge-gray';
  };

  // Calculate stats
  const stats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    inactive: clients.filter(c => c.status === 'inactive').length,
    prospect: clients.filter(c => c.status === 'prospect').length,
  };

  // Filter clients
  const filteredClients = filterStatus === 'all' 
    ? clients 
    : clients.filter(c => c.status === filterStatus);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Clients | CR Hood Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Client
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setFilterStatus('all')}
            className={`p-4 rounded-xl text-center transition-all ${
              filterStatus === 'all' 
                ? 'bg-accent text-white shadow-lg' 
                : 'bg-white hover:bg-gray-50 shadow'
            }`}
          >
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className={`text-sm ${filterStatus === 'all' ? 'text-white/80' : 'text-gray-500'}`}>
              Total
            </div>
          </button>
          
          <button
            onClick={() => setFilterStatus('active')}
            className={`p-4 rounded-xl text-center transition-all ${
              filterStatus === 'active' 
                ? 'bg-green-500 text-white shadow-lg' 
                : 'bg-white hover:bg-gray-50 shadow'
            }`}
          >
            <div className="text-2xl font-bold">{stats.active}</div>
            <div className={`text-sm ${filterStatus === 'active' ? 'text-white/80' : 'text-gray-500'}`}>
              Active
            </div>
          </button>
          
          <button
            onClick={() => setFilterStatus('prospect')}
            className={`p-4 rounded-xl text-center transition-all ${
              filterStatus === 'prospect' 
                ? 'bg-blue-500 text-white shadow-lg' 
                : 'bg-white hover:bg-gray-50 shadow'
            }`}
          >
            <div className="text-2xl font-bold">{stats.prospect}</div>
            <div className={`text-sm ${filterStatus === 'prospect' ? 'text-white/80' : 'text-gray-500'}`}>
              Prospect
            </div>
          </button>
          
          <button
            onClick={() => setFilterStatus('inactive')}
            className={`p-4 rounded-xl text-center transition-all ${
              filterStatus === 'inactive' 
                ? 'bg-gray-500 text-white shadow-lg' 
                : 'bg-white hover:bg-gray-50 shadow'
            }`}
          >
            <div className="text-2xl font-bold">{stats.inactive}</div>
            <div className={`text-sm ${filterStatus === 'inactive' ? 'text-white/80' : 'text-gray-500'}`}>
              Inactive
            </div>
          </button>
        </div>

        {/* Clients List */}
        <div className="card p-0 overflow-hidden">
          {filteredClients.length === 0 ? (
            <div className="text-center py-12">
              {filterStatus === 'all' ? (
                <>
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-gray-500 font-medium">No clients yet</p>
                  <p className="text-sm text-gray-400 mt-1">Convert inquiries or add clients manually</p>
                  <button 
                    onClick={() => setShowAddModal(true)}
                    className="btn btn-primary mt-4"
                  >
                    Add Your First Client
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-500">No {filterStatus} clients</p>
                  <button 
                    onClick={() => setFilterStatus('all')}
                    className="text-accent hover:underline text-sm mt-2"
                  >
                    View all clients
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id}>
                      <td>
                        <Link 
                          to={`/clients/${client.id}`}
                          className="font-medium text-gray-900 hover:text-accent"
                        >
                          {client.name}
                        </Link>
                      </td>
                      <td className="text-gray-600">{client.company || '—'}</td>
                      <td>
                        <a 
                          href={`mailto:${client.email}`}
                          className="text-gray-600 hover:text-accent"
                        >
                          {client.email}
                        </a>
                      </td>
                      <td className="text-gray-600">{client.phone || '—'}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(client.status)}`}>
                          {client.status}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={`/clients/${client.id}`}
                          className="text-accent hover:underline text-sm"
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold mb-4">Add New Client</h2>
            <form onSubmit={handleAddClient}>
              <div className="space-y-4">
                <div>
                  <label className="form-label">Contact Name *</label>
                  <input
                    type="text"
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Company</label>
                  <input
                    type="text"
                    value={newClient.company}
                    onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <select
                    value={newClient.status}
                    onChange={(e) => setNewClient({ ...newClient, status: e.target.value })}
                    className="form-input"
                  >
                    <option value="active">Active</option>
                    <option value="prospect">Prospect</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn btn-primary"
                >
                  {saving ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
